import { useState, useEffect, useRef, useCallback } from "react";
import { api, apiError, downloadBlob } from "@/lib/api";
import { MapGrid } from "@/components/MapViewer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Images, ArrowRight, ArrowLeft, Layers, Crosshair, LayoutGrid, Gem, Waves, Target } from "lucide-react";
import { cleanName } from "@/lib/format";
import GeologyPanel from "@/components/wizard/GeologyPanel";
import MapSuiteDialog from "@/components/MapSuiteDialog";
import MineralAnalysisDialog from "@/components/MineralAnalysisDialog";
import GeomorphologyDialog from "@/components/GeomorphologyDialog";
import TargetStoryDialog from "@/components/TargetStoryDialog";
import { areaIsHms } from "@/lib/hms";
import { MAP_KEYS } from "@/components/wizard/wizardShared";

export default function Step6TargetReview({ project, reload, onNext, onBack }) {
  const [areas, setAreas] = useState(project.areas || []);
  const [viewer, setViewer] = useState(null);
  const [geoArea, setGeoArea] = useState(null);
  const [suiteArea, setSuiteArea] = useState(null);
  const [mineralArea, setMineralArea] = useState(null);
  const [geomorphArea, setGeomorphArea] = useState(null);
  const [storyArea, setStoryArea] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { setAreas(project.areas || []); }, [project.areas]);

  const anyBusy = areas.some((a) => ["queued", "processing"].includes(a.minerals_status));
  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/areas/status`);
      setAreas((prev) => prev.map((a) => {
        const s = data.areas.find((x) => x.area_id === a.area_id);
        return s ? { ...a, ...s } : a;
      }));
      if (!data.areas.some((a) => ["queued", "processing"].includes(a.minerals_status))) {
        clearInterval(pollRef.current); pollRef.current = null; await reload();
      }
    } catch (_) { /* silent */ }
  }, [project.id, reload]);

  useEffect(() => {
    if (anyBusy && !pollRef.current) pollRef.current = setInterval(poll, 3000);
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [anyBusy, poll]);

  const viewMaps = async (aid) => {
    try { const { data } = await api.get(`/projects/${project.id}/areas/${aid}/maps`); setViewer({ aid, maps: data.maps || {} }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const complete = areas.filter((a) => a.status === "complete");

  return (
    <div className="space-y-6" data-testid="step6-target-review">
      <div className="min-w-0 max-w-3xl">
        <h2 className="text-lg font-bold text-foreground tracking-tight">Target Review</h2>
        <p className="text-sm text-muted-foreground">
          Review the ranked exploration targets, geology and data confidence for every processed licence, open
          the full 18-map suite, and re-run per-mineral intelligence before moving to reports.
        </p>
      </div>

      {complete.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-xl py-16 text-center text-muted-foreground" data-testid="review-no-complete">
          <Target size={38} className="mx-auto mb-3" />
          No processed licences yet - go back and run <span className="font-semibold text-foreground">Generate All Areas</span> first.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="review-area-cards">
        {complete.map((a) => (
          <div key={a.area_id} className="bg-card border border-border rounded-xl shadow-sm p-4 min-w-0" data-testid={`review-area-card-${a.area_id}`}>
            <div className="flex items-center flex-wrap gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground grid place-items-center font-extrabold text-sm shrink-0">{a.area_id}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground truncate">{cleanName(a.name)}</div>
                <div className="text-[11px] text-muted-foreground" data-testid={`review-summary-${a.area_id}`}>
                  {a.target_count || 0} Targets · {a.high_priority_count || 0} High Priority
                  {a.data_confidence != null && (
                    <> · <span className="font-semibold text-emerald-700" data-testid={`review-confidence-${a.area_id}`}>{Math.round(a.data_confidence)}/100 confidence</span></>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="border-border" onClick={() => setStoryArea(a)} data-testid={`area-targets-${a.area_id}`}>
                <Crosshair size={14} className="mr-1.5" /> Targets
              </Button>
              <Button size="sm" variant="outline" className="border-border" onClick={() => setGeoArea(a)} data-testid={`area-geology-${a.area_id}`}>
                <Layers size={14} className="mr-1.5" /> Geology
              </Button>
              <Button size="sm" variant="outline" className="border-border" onClick={() => viewMaps(a.area_id)} data-testid={`area-view-maps-${a.area_id}`}>
                <Images size={14} className="mr-1.5" /> View Maps
              </Button>
              <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setSuiteArea(a)} data-testid={`area-suite-${a.area_id}`}>
                <LayoutGrid size={14} className="mr-1.5" /> 18-Map Suite
              </Button>
              <Button size="sm" className="text-white hover:opacity-90" style={{ background: "#C20511" }} onClick={() => setMineralArea(a)} data-testid={`area-minerals-${a.area_id}`}>
                <Gem size={14} className="mr-1.5" /> Minerals
              </Button>
              {areaIsHms(a, project) && (
                <Button size="sm" variant="outline" className="border-[#2A6F97] text-[#2A6F97] hover:bg-[#2A6F97]/10" onClick={() => setGeomorphArea(a)} data-testid={`area-geomorph-${a.area_id}`}>
                  <Waves size={14} className="mr-1.5" /> HMS Geomorphology
                </Button>
              )}
            </div>

            {a.minerals_status === "processing" && (
              <div className="mt-3 text-[11px] text-[#7a5b1e] flex items-center gap-1.5" data-testid={`review-minerals-progress-${a.area_id}`}>
                <Loader2 size={12} className="animate-spin" /> Minerals: {a.minerals_done || 0}/{a.minerals_total || 0} · analysing {a.minerals_current || "…"}
              </div>
            )}
            {a.minerals_status === "complete" && (a.minerals_list || a.minerals || []).length > 0 && (
              <div className="mt-3" data-testid={`review-minerals-done-${a.area_id}`}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Mineral reports (PDF)</div>
                <div className="flex flex-wrap gap-1.5">
                  {(a.minerals_list || a.minerals || []).map((m) => (
                    <button key={m} data-testid={`review-mineral-report-${a.area_id}-${m}`}
                      onClick={() => downloadBlob(`/projects/${project.id}/areas/${a.area_id}/minerals/${encodeURIComponent(m)}/report.pdf`, `${a.area_id}_${m}_report.pdf`).catch((e) => toast.error(apiError(e)))}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100">
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" className="border-border h-11 px-5 text-sm font-semibold" onClick={onBack} data-testid="review-back-btn">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
        <Button data-testid="to-downloads-btn" onClick={onNext} disabled={complete.length === 0}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-sm font-semibold disabled:opacity-40">
          Reports &amp; Delivery <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-[92vw] w-[92vw] max-h-[92vh] p-0 overflow-hidden flex flex-col" data-testid="area-maps-dialog">
          <DialogTitle className="px-6 py-4 border-b border-border dark:border-slate-700 text-foreground dark:text-slate-100 font-bold shrink-0">
            {viewer?.aid} - {viewer?.name || "Maps"}
          </DialogTitle>
          <div className="bg-muted dark:bg-slate-900 p-4 overflow-auto">
            {viewer && (
              <MapGrid items={MAP_KEYS} maps={viewer.maps}
                onHd={(key) => downloadBlob(`/projects/${project.id}/areas/${viewer.aid}/map/${key}`, `${viewer.aid}_${key}.png`)} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <GeologyPanel project={project} area={geoArea} onClose={() => setGeoArea(null)} reload={reload} />
      <MapSuiteDialog open={!!suiteArea} onOpenChange={(o) => !o && setSuiteArea(null)}
        projectId={project.id} areaId={suiteArea?.area_id} areaName={suiteArea?.name} />
      <MineralAnalysisDialog open={!!mineralArea} onOpenChange={(o) => !o && setMineralArea(null)}
        projectId={project.id} area={mineralArea} />
      <GeomorphologyDialog open={!!geomorphArea} onOpenChange={(o) => !o && setGeomorphArea(null)}
        projectId={project.id} areaId={geomorphArea?.area_id} areaName={geomorphArea?.name} />
      <TargetStoryDialog open={!!storyArea} onOpenChange={(o) => !o && setStoryArea(null)}
        areaName={storyArea?.name || storyArea?.area_id} targets={storyArea?.targets || []} evidence={storyArea?.evidence} />
    </div>
  );
}
